/**
 * Client-side validation for the product page's photo-upload block, before
 * the file ever leaves the browser: rejects HEIC (most browsers can't
 * decode it for print/preview) and anything under a minimum width, using
 * the handoff's verbatim copy. Also keeps the upload input and the "skip -
 * send later" checkbox mutually exclusive, since both submit under the same
 * `properties[Photo]` field name (an empty file input contributes nothing,
 * so checking skip cleanly becomes the fallback value when no file is
 * chosen).
 *
 * DOM contract: `[data-photo-upload-input]` (file input, carries
 * `data-min-width`, `data-error-too-small`, `data-error-heic`), a sibling
 * `[data-photo-upload-error]` message target, and `[data-photo-skip]`
 * (the skip checkbox) within the same `.product-main__photo-upload` block.
 */
function isHeic(file) {
  const name = file.name.toLowerCase();
  return file.type === 'image/heic' || file.type === 'image/heif' || name.endsWith('.heic') || name.endsWith('.heif');
}

function measureWidth(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image.naturalWidth);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('unreadable-image'));
    };
    image.src = url;
  });
}

async function validatePhotoInput(input) {
  const block = input.closest('.product-main__photo-upload');
  const errorTarget = block?.querySelector('[data-photo-upload-error]');
  const file = input.files?.[0];

  if (!errorTarget) return;
  errorTarget.hidden = true;
  errorTarget.textContent = '';
  if (!file) return;

  if (isHeic(file)) {
    input.value = '';
    errorTarget.hidden = false;
    errorTarget.textContent = input.dataset.errorHeic;
    return;
  }

  const minWidth = Number(input.dataset.minWidth) || 0;
  if (!minWidth) return;

  try {
    const width = await measureWidth(file);
    if (width < minWidth) {
      input.value = '';
      errorTarget.hidden = false;
      errorTarget.textContent = input.dataset.errorTooSmall;
    }
  } catch (error) {
    // Unreadable image (corrupt file, unsupported format): let the server
    // catch it rather than blocking a possibly-valid upload on a client
    // decode quirk.
  }
}

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-photo-upload-input]');
  if (input) {
    validatePhotoInput(input);
    if (input.files?.length) {
      const skip = input.closest('.product-main__photo-upload')?.querySelector('[data-photo-skip]');
      if (skip) skip.checked = false;
    }
    return;
  }

  const skip = event.target.closest('[data-photo-skip]');
  if (skip?.checked) {
    const block = skip.closest('.product-main__photo-upload');
    const fileInput = block?.querySelector('[data-photo-upload-input]');
    const errorTarget = block?.querySelector('[data-photo-upload-error]');
    if (fileInput) fileInput.value = '';
    if (errorTarget) {
      errorTarget.hidden = true;
      errorTarget.textContent = '';
    }
  }
});
